import classNames from 'clsx';
import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import type { Kind } from 'reactApp/types/Tree';
import { AiPhotoImprovingTooltip } from 'reactApp/ui/AiPhotoImprovingTooltip/AiPhotoImprovingTooltip';
import { Avatar } from 'reactApp/ui/Avatar/Avatar';
import { sendDatalistThumbDwh } from 'reactApp/ui/Datalist/DataList.helpers';
import type { IThumb } from 'reactApp/ui/DataListItem/DataListItem.helpers';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { getIconType } from 'reactApp/ui/FileIcon/getIconType';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { ImageCancelable } from 'reactApp/ui/ImageCancelable/ImageCancelable';
import { withToken } from 'reactApp/utils/withToken';

import styles from './FilePic.css';

interface PicProps {
    className?: string;
    isFolder?: boolean;
    thumbUrl?: IThumb;
    id?: string | undefined;
    ext?: string;
    isVirus?: boolean;
    isLarge?: boolean;
    author?: { name?: string; email: string };
    kind?: Kind;
    isAutoDelete?: boolean;
    isAuthorView?: boolean;
    publicExpires?: number;
    isPublic?: boolean;
    isDark?: boolean;
    isShowAiPhotoImprovingTooltip?: boolean;
    onPhotoImprovingOpen?: (event: any) => void;
}

export const FilePic = forwardRef<HTMLDivElement, PicProps>(function Pic(
    {
        className,
        isFolder = false,
        kind,
        ext,
        isVirus,
        isAutoDelete,
        isAuthorView,
        author,
        thumbUrl,
        publicExpires,
        isPublic = false,
        isDark = false,
        isShowAiPhotoImprovingTooltip,
        onPhotoImprovingOpen,
        id,
    },
    ref
) {
    const iconType = getIconType({
        isFolder,
        kind: isFolder ? kind : undefined,
        ext: isFolder ? undefined : ext,
        isVirus: isFolder ? false : isVirus,
        isAutoDelete: isFolder ? isAutoDelete : false,
        publicExpires,
        isPublic,
        id,
    });

    const [isLoaded, setLoaded] = useState<boolean>(false);

    const src = withToken(thumbUrl?.src);
    const srcSet = withToken(thumbUrl?.srcSet);

    const startTime = useRef(performance.now());

    const handleThumbLoad = useCallback(() => {
        setLoaded(true);

        const endTime = performance.now();

        sendDatalistThumbDwh('load', EViewMode.list, kind, Math.round(endTime - startTime.current));
    }, [kind]);

    const handleThumbError = useCallback(() => {
        const endTime = performance.now();

        sendDatalistThumbDwh('fail', EViewMode.list, kind, Math.round(endTime - startTime.current), src);
    }, [kind, src]);

    if (isAuthorView && author) {
        const { email, name } = author;

        return (
            <div ref={ref} className={styles.root}>
                <Hint text={name || email}>
                    <Avatar size={26} email={email} name={name} />
                </Hint>
            </div>
        );
    }

    return (
        <div ref={ref} className={classNames(styles.root, className)}>
            {author && (
                <Hint text={author.name || author.email}>
                    <div className={styles.author} data-qa-id="author">
                        <Avatar size={20} email={author.email} name={author.name} />
                    </div>
                </Hint>
            )}
            <div className={styles.container}>
                {isShowAiPhotoImprovingTooltip && (
                    <div className={styles.aiIconContainer}>
                        <AiPhotoImprovingTooltip size={20} hasBorder onPhotoImprovingOpen={onPhotoImprovingOpen} />
                    </div>
                )}
                {!isLoaded && (
                    <div className={classNames(styles.icon)}>
                        <FileIcon size="s" type={iconType} mode={isDark ? 'dark' : 'light'} />
                    </div>
                )}
                {!isFolder && thumbUrl?.src && (
                    <ImageCancelable
                        className={classNames(styles.thumb, {
                            [styles.thumb_loaded]: isLoaded,
                        })}
                        src={src}
                        srcSet={srcSet ? `${srcSet} 2x` : ''}
                        alt=""
                        onError={handleThumbError}
                        onLoad={handleThumbLoad}
                    />
                )}
            </div>
        </div>
    );
});

FilePic.displayName = 'FilePic';
